import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { SlotMetrics } from "../modules/teleop-session";
import type { CameraFrameTiming, VideoClockEstimate } from "../modules/teleop-state";
import {
  createFramePathMeasurement,
  framePathSummary,
  type FrameMetadata,
  type FramePathSummary,
} from "../modules/teleop-session/media/video-frame-path";

export function useVideoFramePath(
  videoRef: RefObject<HTMLVideoElement | null>,
  stream: MediaStream | null,
  mount: string,
  readFrameTiming: ((mount: string) => CameraFrameTiming | undefined) | undefined,
  clock: VideoClockEstimate | null | undefined,
  metrics: SlotMetrics | undefined,
  presentationHidden: boolean
) {
  const [measurement] = useState(() => createFramePathMeasurement(performance.timeOrigin));
  const [report, setReport] = useState<FramePathSummary | null>(null);
  const current = useRef({ mount, readFrameTiming, clock, metrics, presentationHidden });
  current.current = { mount, readFrameTiming, clock, metrics, presentationHidden };

  const visible = useCallback(() => {
    const el = videoRef.current;
    if (
      !el ||
      current.current.presentationHidden ||
      document.visibilityState !== "visible" ||
      el.paused
    )
      return false;
    const rect = el.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < innerHeight &&
      rect.left < innerWidth
    );
  }, [videoRef]);

  useEffect(() => {
    measurement.stop("Поток изменился; запустите новый замер");
  }, [measurement, stream]);

  useEffect(() => {
    let publishedTerminal: string | null = null;
    const id = setInterval(() => {
      const c = current.current;
      const timing = c.readFrameTiming?.(c.mount);
      measurement.ingest(timing?.producerId, timing?.sentFrames);
      const fresh = c.metrics?.browserStatsAt && Date.now() - c.metrics.browserStatsAt < 3500;
      measurement.tick(visible(), fresh ? c.metrics?.decodedFps : null);
      const snapshot = measurement.snapshot();
      if (!snapshot) return;
      const terminal =
        snapshot.status === "complete" || snapshot.status === "cancelled"
          ? `${snapshot.startedAtIso}/${snapshot.status}`
          : null;
      if (terminal && terminal === publishedTerminal) return;
      publishedTerminal = terminal;
      setReport(framePathSummary(snapshot));
    }, 1000);
    return () => clearInterval(id);
  }, [measurement, visible]);

  const onFrame = useCallback(
    (now: number, metadata: FrameMetadata) => {
      const c = current.current;
      measurement.frame(metadata, now, c.metrics?.producerId, c.clock, visible());
    },
    [measurement, visible]
  );

  const start = useCallback(() => {
    const c = current.current;
    const timing = c.readFrameTiming?.(c.mount);
    measurement.ingest(timing?.producerId, timing?.sentFrames);
    measurement.start();
    setReport(framePathSummary(measurement.snapshot()));
  }, [measurement]);
  const stop = useCallback(() => {
    measurement.stop();
    setReport(framePathSummary(measurement.snapshot()));
  }, [measurement]);
  const exportReport = useCallback(() => measurement.snapshot(), [measurement]);
  return { report, onFrame, start, stop, exportReport };
}
